import React from "react";
import { Link } from "react-router-dom";
import { type TicketListItem } from "../../../../api/tickets";
import { type TicketDisplay } from "./TicketTable";

type Props = {
  ticket: TicketListItem;
  display?: TicketDisplay;
};

const statusColors: Record<string, string> = {
  OPEN: "bg-amber-50 text-amber-800 ring-1 ring-amber-100",
  IN_PROGRESS: "bg-blue-50 text-blue-800 ring-1 ring-blue-100",
  RESOLVED: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100",
  CLOSED: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
};

const ageLabel = (iso: string | null) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const badgeTone: Record<string, string> = {
  OPEN: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
  IN_PROGRESS: "bg-blue-50 text-blue-700 ring-1 ring-blue-100",
  RESOLVED: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
  CLOSED: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
};

const severityTone: Record<number, string> = {
  1: "bg-yellow-50 text-gray-600 ring-1 ring-gray-100",
  2: "bg-red-50 text-red-800 ring-1 ring-red-100",
  3: "bg-purple-50 text-purple-800 ring-1 ring-purple-100",
};

export default function TicketRow({ ticket, display }: Props) {
  const siteLabel = display?.siteName || (ticket as any).site_name || `Site ${ticket.site_id}`;
  const inverterLabel =
    display?.inverterName ||
    (ticket as any).inverter_name ||
    (ticket.array_inverter_id ? `Inverter ${ticket.array_inverter_id}` : "—");
  const transformerLabel = display?.transformerName || (ticket as any).transformer_name || (ticket.array_id ? `Array ${ticket.array_id}` : "—");
  const openedLabel = ticket.opened_at ? new Date(ticket.opened_at).toLocaleString() : "—";
  const openedAge = ticket.opened_at ? `Opened ${ageLabel(ticket.opened_at)} ago` : "Opened time unavailable";
  const description = ticket.diagnosis_message || (ticket.notes ?? "");
  const descriptionLabel = description ? description : "Not provided";
  const shouldShowNotes = Boolean(ticket.notes && ticket.diagnosis_message);

  return (
    <li className="group">
      <Link
        to={`/maintenance/tickets/${ticket.id}`}
        className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
      >
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
            badgeTone[ticket.status] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
          }`}
        >
          {ticket.id}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-slate-900">Ticket #{ticket.id}</p>
            {ticket.severity_opened != null && (
              <span
                className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                  severityTone[ticket.severity_opened] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
                }`}
              >
                Severity {ticket.severity_opened}
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-700">
            <span>
              <span className="font-semibold text-slate-900">Site:</span> {siteLabel}
            </span>
            <span>
              <span className="font-semibold text-slate-900">Transformer:</span> {transformerLabel}
            </span>
            <span>
              <span className="font-semibold text-slate-900">Inverter:</span> {inverterLabel}
            </span>
          </div>

          <p className="text-xs text-slate-700 truncate">
            <span className="font-semibold text-slate-900">Error description:</span> {descriptionLabel}
          </p>
          {shouldShowNotes && <p className="text-xs text-slate-500 truncate">{ticket.notes}</p>}
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex sm:flex-col sm:items-end sm:gap-1 text-right">
            <span
              className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                statusColors[ticket.status] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200"
              }`}
            >
              {ticket.status}
            </span>
            <p className="text-xs text-slate-500">{openedLabel}</p>
            <p className="text-xs text-slate-500">{openedAge}</p>
          </div>

          <svg
            className="h-5 w-5 text-slate-400 transition-colors group-hover:text-slate-600"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 5l5 5-5 5" />
          </svg>
        </div>
      </Link>
    </li>
  );
}
